
import { OfferBadge } from '../OfferBadge/OfferBadge.jsx'

import styles from './PriceBlock.module.scss'

/**
 * PriceBlock — displays a price field with optional label, strikethrough, and offer badge.
 *
 * Accepts pre-computed price values (from `formatProductPrice` or equivalent).
 * Zero domain logic — renders only what it receives.
 *
 * Typical usage (with label, for tiles):
 *   <PriceBlock label="Cena brutto" displayPrice="322 816,00 zł"
 *               strikethroughPrice="335 232,00 zł" isOnOffer highlight />
 *
 * Usage without label (for table cells, where label is in column header):
 *   <PriceBlock displayPrice="322 816,00 zł" />
 *
 * @param {object}       props
 * @param {string|null}  props.displayPrice            - Formatted price string
 * @param {string|null}  [props.strikethroughPrice]    - Previous price (struck through)
 * @param {boolean}      [props.isOnOffer=false]       - Offer styling: red price + badge + strikethrough
 * @param {boolean}      [props.isHidden=false]        - Show dash instead of price (hidden price)
 * @param {string}       [props.label]                 - Optional label above the price (e.g. "Cena brutto")
 * @param {boolean}      [props.highlight=false]       - Subtle background container
 * @param {string}       [props.className]             - Extra class on root element
 */
export function PriceBlock({
  displayPrice,
  strikethroughPrice,
  isOnOffer   = false,
  isHidden    = false,
  label,
  highlight   = true,
  className,
}) {
  const rootClass = [
    styles.priceBox,
    highlight ? styles.highlighted : '',
    className,
  ].filter(Boolean).join(' ')

  return (
    <div className={rootClass}>
      <div className={styles.labelRow}>
        {label && <span className={styles.label}>{label}</span>}
        {isOnOffer && (
          <OfferBadge
          size="tiny"
          />
        )}
      </div>

      {isHidden ? (
        <span className={styles.hidden}>—</span>
      ) : (
        <div className={styles.row}>
          {isOnOffer && strikethroughPrice && (
            <span className={styles.strikethrough}>{strikethroughPrice}</span>
          )}
          <span className={[styles.price, isOnOffer ? styles.offerPrice : ''].filter(Boolean).join(' ')}>
            {displayPrice ?? '—'}
          </span>
        </div>
      )}
    </div>
  )
}
